import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import clsx from "clsx";
import {
  Card,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from "@material-ui/core";
import LoadingButton from "app/main/shared-components/loading-button/LoadingButton";
import useStyles from "./login-page.styles";
import authService from "app/services/auth.service";

import LandingImage from "./landing.jpg";
import Logo from "./Bemyticket.png";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

interface State {
  newPassword: string;
  confirmNewPassword: string;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
}

const ResetPassword = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();

  const params = new URLSearchParams(location.search);
  const token = params.get("token");

  useEffect(() => {
    if (!token) {
      history.push("/login");
    }
  }, [token, history]);

  const [values, setValues] = useState<State>({
    newPassword: "",
    confirmNewPassword: "",
    showNewPassword: false,
    showConfirmPassword: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange =
    (prop: keyof State) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues({ ...values, [prop]: event.target.value });
    };

  const handleClickShowNewPassword = () =>
    setValues((prev) => ({ ...prev, showNewPassword: !prev.showNewPassword }));
  const handleClickShowConfirmPassword = () =>
    setValues((prev) => ({
      ...prev,
      showConfirmPassword: !prev.showConfirmPassword,
    }));
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => event.preventDefault();

  const passwordMismatch =
    values.newPassword !== values.confirmNewPassword &&
    values.confirmNewPassword.length > 0;

  const resetPasswordHandler = async () => {
    if (passwordMismatch || !token) return;

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await authService.resetPassword({
        token,
        newPassword: values.newPassword,
        confirmNewPassword: values.confirmNewPassword,
      });
      setSuccess(true);
      setValues((prev) => ({ ...prev, newPassword: "", confirmNewPassword: "" }));
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  const onEnterHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") resetPasswordHandler();
  };

  return (
    <div
      className={classes.root}
      style={{
        background: `url(${LandingImage})`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Card className={classes.card}>
        <div className={classes.logoWrapper}>
          <img src={Logo} alt="logo" width={"270px"} />
        </div>

        <div style={{ textAlign: "center", marginBottom: "1rem" }}>
          <h2 style={{ color: "#333", marginBottom: "0.5rem" }}>Reset Password</h2>
          <p style={{ color: "#666", fontSize: "14px" }}>Enter your new password</p>
        </div>

        {/* New Password */}
        <FormControl
          size="small"
          className={clsx(classes.margin, classes.textField)}
          variant="outlined"
        >
          <InputLabel htmlFor="outlined-adornment-new-password">New Password</InputLabel>
          <OutlinedInput
            id="outlined-adornment-new-password"
            type={values.showNewPassword ? "text" : "password"}
            value={values.newPassword}
            onChange={handleChange("newPassword")}
            onKeyUp={onEnterHandler}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowNewPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  size="small"
                >
                  {values.showNewPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            labelWidth={105}
          />
        </FormControl>

        {/* Confirm Password */}
        <FormControl
          size="small"
          className={clsx(classes.margin, classes.textField)}
          variant="outlined"
        >
          <InputLabel htmlFor="outlined-adornment-confirm-password">Confirm Password</InputLabel>
          <OutlinedInput
            id="outlined-adornment-confirm-password"
            type={values.showConfirmPassword ? "text" : "password"}
            value={values.confirmNewPassword}
            onChange={handleChange("confirmNewPassword")}
            onKeyUp={onEnterHandler}
            error={passwordMismatch}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowConfirmPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  size="small"
                >
                  {values.showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            labelWidth={130}
          />
        </FormControl>

        {passwordMismatch && (
          <p style={{ textAlign: "center", color: "red", fontSize: "12px" }}>
            Passwords do not match
          </p>
        )}
        {error && (
          <p style={{ textAlign: "center", color: "red", marginBottom: "10px" }}>
            {error}
          </p>
        )}
        {success && (
          <p style={{ textAlign: "center", color: "green", marginBottom: "10px" }}>
            Password has been reset successfully
          </p>
        )}

        <FormControl>
          <div className={classes.btnWrapper}>
            <LoadingButton
              btnTitle="Reset Password"
              onClick={resetPasswordHandler}
              isLoading={isLoading}
            />
          </div>
        </FormControl>

        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <a
            href="/login"
            style={{ color: "#1976d2", textDecoration: "none", fontSize: "14px" }}
          >
            Back to Login
          </a>
        </div>
      </Card>
    </div>
  );
};

export default ResetPassword;